import React, { PureComponent } from 'react';
import moment from 'moment';
import { Helmet } from 'react-helmet';

import LocalizedMessage, { localizeMessage } from '../../../../components/LocalizedMessage';
import URLFiltersHoc, { Props } from '../../../../components/URLFiltersHoc';
import Select from '../../../../components/Select';
import Breadcrumbs from '../../../../components/Breadcrumbs';
import TableList from '../../../../components/TableList';

import Alert from '../../../../helpers/alert';
import API from '../../../../api';
import { IOption } from 'types';

interface IState {
  dataList: any[],
  dataListTotal: number;
  isLoading: boolean;

  yearValue: any;
  rubValue: any;
  yearOptions: IOption[];
}

class Currencies extends PureComponent<Props, IState> {
  years = {
    startYear: moment().add(1, 'year').year(),
    endYear: moment().add(-5, 'year').year()
  };

  state: IState = {
    dataList: [],
    dataListTotal: 1,
    isLoading: false,

    yearValue: null,
    rubValue: '',
    yearOptions: this.getYearOptions()
  };

  mounted = false;

  refreshListTimer = 0;
  refreshListTimerDelay = 500;
  _form = React.createRef<HTMLFormElement>();

  componentDidMount () {
    this.mounted = true;
    this.refreshList();
  }

  componentDidUpdate (prevProps) {
    if (prevProps.filters !== this.props.filters) {
      this.refreshList();
    }
  }

  componentWillUnmount () {
    this.mounted = false;
    clearTimeout(this.refreshListTimer);
  }

  getYearOptions () {
    const years: IOption[] = [];

    for (let year = this.years.startYear; year >= this.years.endYear; year--) {
      years.push({
        value: year,
        label: String(year)
      });
    }

    return years;
  }

  refreshList () {
    clearTimeout(this.refreshListTimer);
    const { currentPage, currentSort, max } = this.props.filters;
    const dataListRequest = {
      page: currentPage,
      order: currentSort,
      max,
    };

    this.refreshListTimer = window.setTimeout(async () => {
      this.setState({ isLoading: true });

      const dataList = await API.currencies.list(dataListRequest);

      if (!this.mounted) {
        return;
      }

      this.setState({
        dataList: dataList.items,
        dataListTotal: dataList.total,
        isLoading: false
      });
    }, this.refreshListTimerDelay);
  }

  getBreadcrumbs () {
    return [
      {
        title: <LocalizedMessage id='home' />,
        link: '/app'
      },
      {
        title: <LocalizedMessage id='currencies-dictionary.title' />,
        link: '/app/dictionaries/currencies'
      }
    ];
  }

  setFormRef = (ref) => {
    this._form = ref;
  };

  handleChangeYearSelect = (yearValue) => {
    this.setState({ yearValue });
  };

  getSelectYearConfig () {
    const { yearValue, yearOptions } = this.state;

    return {
      isSearchable: false,
      isMulti: false,
      value: yearValue,
      onChange: this.handleChangeYearSelect,
      options: yearOptions,
      placeholder: <LocalizedMessage id='currencies-dictionary.year.placeholder' />
    };
  }

  handleInputChange = (e) => {
    const value = e.target.value;

    this.setState({
      rubValue: value
    });
  };

  resetClick = () => {
    if (this._form.current) {
      this._form.current.reset();
    }

    this.setState({
      yearValue: null,
      rubValue: ''
    });
  };

  onSubmit = async (e) => {
    e.preventDefault();

    const {
      yearValue,
      rubValue
    } = this.state;

    if (
      (!yearValue || !yearValue.value) ||
      (!rubValue)
    ) {
      return;
    }

    try {
      await API.currencies.update({
        year: yearValue.value,
        eur: rubValue
      });

      Alert.success(localizeMessage({ id: 'currencies-dictionary.successfullySent' }));

      this.resetClick();

      this.refreshList();
    } catch (error) {
      console.error(error);

      Alert.warn(localizeMessage({ id: 'currencies-dictionary.errorSending' }));
    }
  };

  render () {
    const {
      onUpdateSort, onUpdatePage, filters,
    } = this.props;
    const {
      yearValue,
      rubValue,
      dataList,
      dataListTotal,
      isLoading
    } = this.state;

    return (
      <>
        <LocalizedMessage
          id='site.title.dictionaries.currencies'
        >
          {localizedMessage => (
            <>
              <Helmet
                title={localizedMessage}
              />
              <Breadcrumbs
                title={localizedMessage}
                data={this.getBreadcrumbs()}
              />
            </>
          )}
        </LocalizedMessage>

        <div className='wrapper wrapper-content'>
          <div className='row'>
            <div className='col-lg-12'>
              <div className='ibox float-e-margins'>
                <div className='ibox-content'>
                  <form
                    ref={this.setFormRef}
                    onSubmit={this.onSubmit}
                  >
                    <div className='form-horizontal'>
                      <div className='form-group'>
                        <label className='col-lg-2 control-label' htmlFor='year'><LocalizedMessage id='year' /></label>

                        <div className='col-lg-10'>
                          <Select {...this.getSelectYearConfig()} />
                        </div>
                      </div>

                      <div className='form-group'>
                        <label className='col-lg-2 control-label'>1eur =</label>

                        <div className='col-lg-10'>
                          <LocalizedMessage id='currencies.rub'>
                            {localizedPlaceholder => (
                              <input
                                type='number'
                                step='any'
                                className='form-control'
                                name='rubValue'
                                value={rubValue}
                                placeholder={localizedPlaceholder}
                                onChange={this.handleInputChange}
                              />
                            )}
                          </LocalizedMessage>
                        </div>
                      </div>

                      <div className='hr-line-dashed' />

                      <div className='form-group'>
                        <div className='col-lg-2' />
                        <div className='col-lg-10'>
                          <button
                            className='btn btn-white'
                            onClick={this.resetClick}
                          >
                            <span><LocalizedMessage id='reset' /></span>
                          </button>
                          &nbsp;&nbsp;
                          <button
                            type='submit'
                            className='btn btn-primary'
                            onClick={this.onSubmit}
                            disabled={!yearValue || !rubValue}
                          >
                            <>
                              <i className='fa fa-chevron-down fa-lg' />
                              <span><LocalizedMessage id='save' /></span>
                            </>
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className='row'>
            <div className='col-lg-12'>
              <div className='ibox float-e-margins'>
                <div className='ibox-content'>
                  <div className='table-responsive'>
                    <LocalizedMessage id={['year', 'currencies.rub']}>
                      {(localizedYear, localizedRub) => (
                        <TableList
                          currentPage={filters.currentPage}
                          currentSort={filters.currentSort}
                          totalItems={dataListTotal}
                          itemsCountPerPage={filters.max}
                          onUpdateSort={onUpdateSort}
                          onUpdatePage={onUpdatePage}
                          isLoading={isLoading}
                          head={[
                            {
                              label: localizedYear,
                              sort: 'year'
                            },
                            {
                              label: localizedRub,
                              sort: 'value'
                            }
                          ]}
                        >
                          {
                            dataList.map((row, rowIndex) => (
                              <tr key={rowIndex + '_currencies'}>
                                <td>{row.year}</td>
                                <td>{row.eur}</td>
                              </tr>
                            ))
                          }
                        </TableList>
                      )}
                    </LocalizedMessage>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default URLFiltersHoc(Currencies, 'year');
